<div class="max-w-2xl mx-auto p-6">
  <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
    <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-4">Profile Settings</h1>

    <!-- User Info & Membership Badge -->
    <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-8 flex flex-col sm:flex-row sm:items-center gap-3">
      <div class="flex-1">
        @if (userProfile(); as profile) {
          <p class="text-base font-semibold text-gray-900 dark:text-white">{{ profile.firstName }} {{ profile.lastName }}</p>
          <p class="text-sm text-gray-500 dark:text-gray-400">{{ profile.email || userEmail() }}</p>
        } @else {
          <p class="text-sm text-gray-500 dark:text-gray-400">{{ userEmail() }}</p>
        }
      </div>
      <span class="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold capitalize self-start sm:self-auto"
        [class.bg-gray-200]="rolesService.highestRole() === 'free'"
        [class.text-gray-700]="rolesService.highestRole() === 'free'"
        [class.bg-blue-100]="rolesService.highestRole() === 'standard'"
        [class.text-blue-700]="rolesService.highestRole() === 'standard'"
        [class.bg-yellow-100]="rolesService.highestRole() === 'pro'"
        [class.text-yellow-700]="rolesService.highestRole() === 'pro'"
        [class.bg-green-100]="rolesService.highestRole() === 'admin'"
        [class.text-green-700]="rolesService.highestRole() === 'admin'">
        <i class="pi pi-crown text-xs"></i>
        {{ rolesService.highestRole() }} plan
      </span>
    </div>

    <!-- Loading State -->
    @if (isLoading()) {
      <div class="space-y-6">
        <div class="animate-pulse">
          <div class="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/4 mb-2"></div>
          <div class="h-10 bg-gray-300 dark:bg-gray-600 rounded"></div>
        </div>
        <div class="animate-pulse">
          <div class="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/4 mb-2"></div>
          <div class="h-10 bg-gray-300 dark:bg-gray-600 rounded"></div>
        </div>
        <div class="animate-pulse">
          <div class="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/4 mb-2"></div>
          <div class="h-10 bg-gray-300 dark:bg-gray-600 rounded"></div>
        </div>
      </div>
    } @else {
      <!-- Profile Form -->
      <form [formGroup]="profileForm" (ngSubmit)="onSubmit()" class="space-y-6">
        <!-- Email Field (Read-only) -->
        <div>
          <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            [value]="userEmail()"
            readonly
            class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed focus:outline-none focus:ring-0"
          />
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Email cannot be changed. Contact support if you need to update your email address.
          </p>
        </div>

        <!-- First Name Field -->
        <div>
          <label for="firstName" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            First Name
          </label>
          <input
            id="firstName"
            type="text"
            formControlName="firstName"
            placeholder="Enter your first name"
            class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-jamaica-green-500 focus:border-transparent transition-colors"
            [class.border-red-500]="profileForm.get('firstName')?.invalid && profileForm.get('firstName')?.touched"
          />
          @if (profileForm.get('firstName')?.invalid && profileForm.get('firstName')?.touched) {
            <p class="mt-1 text-sm text-red-600 dark:text-red-400">
              @if (profileForm.get('firstName')?.errors?.['required']) {
                Name is required.
              } @else if (profileForm.get('firstName')?.errors?.['minlength']) {
                Name must be at least 2 characters long.
              }
            </p>
          }
        </div>
          <!-- Last Name Field -->
        <div>
          <label for="lastName" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Last Name
          </label>
          <input
            id="lastName"
            type="text"
            formControlName="lastName"
            placeholder="Enter your last (surname) name"
            class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-jamaica-green-500 focus:border-transparent transition-colors"
            [class.border-red-500]="profileForm.get('lastName')?.invalid && profileForm.get('lastName')?.touched"
          />
          @if (profileForm.get('lastName')?.invalid && profileForm.get('lastName')?.touched) {
            <p class="mt-1 text-sm text-red-600 dark:text-red-400">
              @if (profileForm.get('lastName')?.errors?.['required']) {
                Last name is required.
              } @else if (profileForm.get('lastName')?.errors?.['minlength']) {
                Last name must be at least 2 characters long.
              }
            </p>
          }
        </div>

        <!-- Marketing Consent -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Marketing Preferences
          </label>
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div class="flex items-start">
              <input
                id="marketingConsent"
                type="checkbox"
                formControlName="marketingConsent"
                class="mt-1 h-4 w-4 text-jamaica-green-600 focus:ring-jamaica-green-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <label for="marketingConsent" class="ml-3 text-sm text-gray-700 dark:text-gray-300">
                <span class="font-medium">I agree to receive marketing emails</span>
                <p class="text-gray-500 dark:text-gray-400 mt-1">
                  Get updates about new features, stock insights, and special offers. You can unsubscribe at any time.
                </p>
              </label>
            </div>
          </div>
        </div>

        <!-- Theme Preference -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
            Theme Preference
          </label>
          <div class="flex gap-4">
            <button
              type="button"
              (click)="onThemeChange('light')"
              [class.active]="profileForm.get('themePreference')?.value === 'light'"
              class="flex items-center gap-3 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-jamaica-green-500"
            >
              <i class="fa-solid fa-sun text-yellow-500"></i>
              <span>Light Theme</span>
            </button>
            <button
              type="button"
              (click)="onThemeChange('dark')"
              [class.active]="profileForm.get('themePreference')?.value === 'dark'"
              class="flex items-center gap-3 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-jamaica-green-500"
            >
              <i class="fa-solid fa-moon text-blue-500"></i>
              <span>Dark Theme</span>
            </button>
          </div>
        </div>

        <!-- Success Message -->
        @if (saveSuccess()) {
          <div class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div class="flex items-center">
              <i class="fa-solid fa-check-circle text-green-600 dark:text-green-400 mr-3"></i>
              <span class="text-green-800 dark:text-green-200">Profile updated successfully!</span>
            </div>
          </div>
        }

        <!-- Action Buttons -->
        <div class="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="submit"
            [disabled]="isSaving() || profileForm.invalid"
            class="flex-1 bg-jamaica-green-600 hover:bg-jamaica-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-jamaica-green-500 focus:ring-offset-2"
          >
            @if (isSaving()) {
              <i class="fa-solid fa-spinner fa-spin mr-2"></i>
              Saving...
            } @else {
              <i class="fa-solid fa-save mr-2"></i>
              Save Changes
            }
          </button>

          <button
            type="button"
            (click)="onManageSubscription()"
            [disabled]="isManagingSubscription()"
            class="flex-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-60 disabled:cursor-not-allowed text-gray-900 dark:text-white font-medium py-3 px-6 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            @if (isManagingSubscription()) {
              <i class="fa-solid fa-spinner fa-spin mr-2"></i>
              Redirecting...
            } @else {
              <i class="fa-solid fa-credit-card mr-2"></i>
              Manage Subscription
            }
          </button>
        </div>
      </form>
    }
  </div>
</div>
